/* eslint-disable camelcase */
import React from 'react';
import PropTypes from 'prop-types';
import {
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TablePagination,
  TableRow,
  Paper,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  CircularProgress,
  Chip,
  Box,
  alpha,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { format, parseISO } from 'date-fns';
import Scrollbar from '../scrollbar';
import { UserListHead } from '../../sections/@dashboard/user';
import { ROWS_PER_PAGE_OPTIONS } from '../../utils/contractConstants';
import { getStatusColor } from '../../utils/contractTableUtils';
import { LightTooltip } from '../custom-components/custom-tooltip';

const ContractTable = ({
  contracts,
  isLoading,
  page,
  rowsPerPage,
  order,
  orderBy,
  selected,
  anchorEl,
  onRequestSort,
  onSelectAllClick,
  onPageChange,
  onRowsPerPageChange,
  onActionClick,
  onClose,
  onShowContract,
  TABLE_HEAD,
}) => {
  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - contracts?.length) : 0;

  return (
    <Card sx={{ borderRadius: 2, boxShadow: (theme) => theme.customShadows?.z1 || theme.shadows[1] }}>
      <Scrollbar>
        <TableContainer sx={{ minWidth: 800 }}>
          <Table>
            <UserListHead
              order={order}
              orderBy={orderBy}
              headLabel={TABLE_HEAD}
              rowCount={contracts.length}
              numSelected={selected.length}
              onRequestSort={onRequestSort}
              onSelectAllClick={onSelectAllClick}
            />
            {isLoading ? (
              <TableRow>
                <TableCell align="center" colSpan={8} sx={{ py: 8 }}>
                  <CircularProgress />
                  <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
                    Loading agreements...
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              contracts.length === 0 && (
                <TableBody>
                  <TableRow>
                    <TableCell align="center" colSpan={8} sx={{ py: 10 }}>
                      <Paper
                        sx={{
                          textAlign: 'center',
                          backgroundColor: 'transparent',
                          boxShadow: 'none',
                        }}
                      >
                        <Typography variant="h6" gutterBottom>
                          No Agreements Found
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                          Try adjusting your filters or search terms.
                        </Typography>
                      </Paper>
                    </TableCell>
                  </TableRow>
                </TableBody>
              )
            )}

            <TableBody>
              {contracts.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row, index) => {
                const { id, practice_name, activation_date, created_at, services, covered_entity, status } = row;
                const isSelected = selected.indexOf(practice_name) !== -1;

                return (
                  <TableRow
                    hover
                    key={id}
                    tabIndex={-1}
                    role="checkbox"
                    selected={isSelected}
                    sx={{
                      '&:hover': {
                        backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.04),
                      },
                      cursor: 'pointer',
                    }}
                    onClick={() => onShowContract(id, status, row)}
                  >
                    <TableCell align="left">
                      <Typography variant="subtitle2" noWrap>
                        {practice_name}
                      </Typography>
                    </TableCell>
                    <TableCell align="left">{covered_entity}</TableCell>
                    <TableCell align="left">
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        {format(parseISO(activation_date), 'MMM dd, yyyy')}
                      </Typography>
                    </TableCell>
                    <TableCell
                      align="left"
                      sx={{ maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                    >
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {services.slice(0, 2).map((service, i) => (
                          <Chip
                            key={i}
                            label={service}
                            size="small"
                            variant="outlined"
                            sx={{ fontSize: '0.65rem', height: 20 }}
                          />
                        ))}
                        {services.length > 2 && (
                          <Typography variant="caption" sx={{ color: 'text.secondary', alignSelf: 'center' }}>
                            +{services.length - 2} more
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align="left">
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        {format(parseISO(created_at), 'MMM dd, yyyy')}
                      </Typography>
                    </TableCell>
                    <TableCell align="left">
                      <Chip
                        label={status}
                        size="small"
                        sx={{
                          fontWeight: 600,
                          textTransform: 'uppercase',
                          fontSize: '0.65rem',
                          backgroundColor: (theme) => alpha(getStatusColor(status, theme), 0.12),
                          color: (theme) => getStatusColor(status, theme),
                          border: (theme) => `1px solid ${alpha(getStatusColor(status, theme), 0.24)}`,
                        }}
                      />
                    </TableCell>
                    <TableCell align="left" onClick={(e) => e.stopPropagation()}>
                      <LightTooltip title="Contract Actions" disableHoverListener={Boolean(anchorEl[index])}>
                        <IconButton
                          aria-controls="client-actions-menu"
                          aria-haspopup="true"
                          onClick={(e) => {
                            onActionClick(e, index);
                          }}
                        >
                          <MoreVertIcon />
                        </IconButton>
                      </LightTooltip>
                      <Menu
                        id="client-actions-menu"
                        anchorEl={anchorEl[index]}
                        open={Boolean(anchorEl[index])}
                        onClose={onClose}
                        PaperProps={{
                          sx: {
                            width: 160,
                            boxShadow: 'none',
                            border: (theme) => `1px solid ${theme.palette.divider}`,
                          },
                        }}
                      >
                        <MenuItem onClick={() => onShowContract(id, status, row)} sx={{ typography: 'body2', py: 1.5 }}>
                          <VisibilityIcon sx={{ mr: 2, fontSize: 20, color: 'text.secondary' }} />
                          Show Contract
                        </MenuItem>
                      </Menu>
                    </TableCell>
                  </TableRow>
                );
              })}
              {emptyRows > 0 && (
                <TableRow style={{ height: 53 * emptyRows }}>
                  <TableCell colSpan={8} />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Scrollbar>
      <TablePagination
        rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
        component="div"
        count={contracts?.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={onPageChange}
        onRowsPerPageChange={onRowsPerPageChange}
      />
    </Card>
  );
};

ContractTable.propTypes = {
  contracts: PropTypes.array.isRequired,
  isLoading: PropTypes.bool.isRequired,
  page: PropTypes.number.isRequired,
  rowsPerPage: PropTypes.number.isRequired,
  order: PropTypes.oneOf(['asc', 'desc']).isRequired,
  orderBy: PropTypes.string.isRequired,
  selected: PropTypes.array.isRequired,
  anchorEl: PropTypes.object.isRequired,
  onRequestSort: PropTypes.func.isRequired,
  onSelectAllClick: PropTypes.func.isRequired,
  onPageChange: PropTypes.func.isRequired,
  onRowsPerPageChange: PropTypes.func.isRequired,
  onActionClick: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onShowContract: PropTypes.func.isRequired,
  TABLE_HEAD: PropTypes.array.isRequired,
};

export default ContractTable;
